import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import AppContext from 'Utils/AppContext';
import { getRulesListOptions } from 'DataDiscovery/Utils';
import { buildDiscoveryRequest, startDiscovery } from 'DataDiscovery/discoverApi';
import { createDataSource } from 'DataSources/datasourcesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function lastSegment(path) {
  const p = (path || '').replace(/\/+$/, '');
  const i = p.lastIndexOf('/');
  return i >= 0 ? p.slice(i + 1) : p;
}

const StartDiscoverDialog = ({
  show,
  onHide,
  candidateRow,
  existingSource,
  parentSourceName,
  parentSourceId,
  vfsScheme,
}) => {
  const { rulesList, loadConnectionsHandler, loadSearchStatusesHandler, markDiscoveryStarted, addOptimisticRun, setNotification } = useContext(AppContext);
  const history = useHistory();

  const defaultDsName = useMemo(() => {
    if (existingSource) return existingSource.name || '';
    const folderName = lastSegment(candidateRow?.folder_path || '');
    return parentSourceName ? `${parentSourceName}.${folderName}` : folderName;
  }, [existingSource, candidateRow, parentSourceName]);

  const rulesListOptions = useMemo(() => getRulesListOptions(rulesList || []), [rulesList]);
  const defaultRuleOption = useMemo(() => {
    const found = rulesListOptions.find((o) => o.value && o.value.id === 'DefaultSampling-3pct');
    return found || rulesListOptions[0] || null;
  }, [rulesListOptions]);

  const [dsName, setDsName] = useState('');
  const [discoverName, setDiscoverName] = useState('');
  const [selectedRule, setSelectedRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      setDsName(defaultDsName);
      setDiscoverName(`${defaultDsName} ${todayStr()}`);
      setSelectedRule(defaultRuleOption);
      setError(null);
      setLoading(false);
    }
  }, [show, defaultDsName, defaultRuleOption]);

  const handleStart = async () => {
    if (!discoverName.trim()) {
      setError('Discovery name is required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let sourceId;
      let sourceForRequest;
      if (!existingSource) {
        if (!dsName.trim()) {
          setError('Data source name is required.');
          setLoading(false);
          return;
        }
        const vfsCredentials = {};
        if (candidateRow && candidateRow.delimiter) {
          vfsCredentials.delimiter = candidateRow.delimiter;
        }
        const config = { rootPath: candidateRow ? candidateRow.folder_path : '' };
        if (Object.keys(vfsCredentials).length) config.vfsCredentials = vfsCredentials;
        const dto = {
          name: dsName.trim(),
          datasourceType: candidateRow ? candidateRow.datasource_type : '',
          engine: (vfsScheme || 'LOCAL').toLowerCase(),
          vfsScheme: vfsScheme || 'LOCAL',
          config,
          parentDataSourceId: parentSourceId ?? null,
        };
        const newDs = await createDataSource(dto);
        sourceId = newDs.id;
        sourceForRequest = { id: sourceId };
      } else {
        sourceId = existingSource.id;
        sourceForRequest = existingSource;
      }

      // A run may not start against an unreachable source — verify (or establish) a
      // successful connection test first. On failure the helper routes the user to the
      // source editor to fix the connection; close the dialog behind them.
      const gate = await ensureConnectionTested(sourceId, {
        history,
        setNotification,
        name: existingSource?.name || dsName.trim(),
      });
      if (!gate.ok) {
        setLoading(false);
        onHide();
        return;
      }

      const rule = (selectedRule && selectedRule.value) || (rulesList && rulesList[0]) || null;
      const req = buildDiscoveryRequest(sourceForRequest, rule, discoverName.trim());
      await startDiscovery(req);
      markDiscoveryStarted(sourceId);
      if (typeof addOptimisticRun === 'function') {
        addOptimisticRun({
          searchName: discoverName.trim(),
          connection: existingSource?.name || dsName.trim(),
          type: existingSource?.type || (vfsScheme || 'LOCAL').toLowerCase(),
          dataSourceId: sourceId,
          sampleRules: rule?.name,
        });
      }
      onHide();
      setNotification({
        header: 'Discovery Started',
        body: `Discovery started for "${dsName.trim() || existingSource?.name || ''}".`,
      });
      await Promise.all([loadConnectionsHandler(), loadSearchStatusesHandler()]);
    } catch (e) {
      setError(e && e.message ? e.message : 'Failed to start discovery.');
      setLoading(false);
    }
  };

  const isCreating = !existingSource;
  const canStart = discoverName.trim() !== '' && (isCreating ? dsName.trim() !== '' : true);

  return (
    <Modal show={show} onHide={onHide} backdrop='static' keyboard={!loading}>
      <Modal.Header closeButton={!loading}>
        <Modal.Title style={{ fontFamily: 'Rubik', fontSize: 18 }}>
          Discover
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant='danger' style={{ fontFamily: 'Rubik', fontSize: 14 }}>
            {error}
          </Alert>
        )}
        <Form.Group>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Data Source Name
          </Form.Label>
          <Form.Control
            type='text'
            value={dsName}
            disabled={!isCreating || loading}
            onChange={(e) => setDsName(e.target.value)}
            placeholder='Data source name'
          />
          {isCreating && (
            <Form.Text className='text-muted'>
              A new data source will be created for this folder.
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group className='mt-3'>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Discovery Name
          </Form.Label>
          <Form.Control
            type='text'
            value={discoverName}
            disabled={loading}
            onChange={(e) => setDiscoverName(e.target.value)}
            placeholder='Discovery run name'
          />
        </Form.Group>
        <Form.Group className='mt-3'>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Sampling Rules
          </Form.Label>
          <Select
            options={rulesListOptions}
            value={selectedRule}
            onChange={(opt) => setSelectedRule(opt)}
            isDisabled={loading}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleStart}
          disabled={loading || !canStart}
        >
          {loading ? (
            <>
              <Spinner animation='border' size='sm' className='mr-2' />
              Starting…
            </>
          ) : (
            'Start'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StartDiscoverDialog;
